import { useState, useEffect, useCallback } from 'react';
import { livekitService, LiveKitRoomToken } from '../services/livekitService';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';
import toast from 'react-hot-toast';

export interface UseLiveKitOptions {
  autoConnect?: boolean;
  enableVoice?: boolean;
  enableVideo?: boolean;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: string) => void;
}

export interface UseLiveKitReturn {
  // Connection state
  isConnecting: boolean;
  isConnected: boolean;
  connectionError: string | null;
  
  // Room data
  roomToken: LiveKitRoomToken | null;
  roomName: string | null;
  
  // Actions
  createPersonalChatRoom: (conversationId: string) => Promise<boolean>;
  createGroupChatRoom: (groupId: string, participants: string[]) => Promise<boolean>;
  createVoiceConsultationRoom: (consultationId: string) => Promise<boolean>;
  joinRoom: (roomName: string, participantName?: string) => Promise<boolean>;
  disconnect: () => void;
  resetConnectionState: () => void;
  
  // Utilities
  healthCheck: () => Promise<boolean>;
}

export const useLiveKit = (options: UseLiveKitOptions = {}): UseLiveKitReturn => {
  const { user } = useAuth();
  const {
    autoConnect = false,
    enableVoice = false,
    enableVideo = false,
    onConnect,
    onDisconnect,
    onError,
  } = options;

  // State
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [roomToken, setRoomToken] = useState<LiveKitRoomToken | null>(null);
  const [roomName, setRoomName] = useState<string | null>(null);

  // Reset connection state
  const resetConnectionState = useCallback(() => {
    setIsConnecting(false);
    setIsConnected(false);
    setConnectionError(null);
    setRoomToken(null);
    setRoomName(null);
  }, []);

  // Handle connection success
  const handleConnectionSuccess = useCallback((token: LiveKitRoomToken) => {
    if (isConnected) {
      logger.info('Already connected, skipping duplicate connection');
      return;
    }
    
    setIsConnecting(false);
    setIsConnected(true);
    setConnectionError(null);
    setRoomToken(token);
    setRoomName(token.roomName);
    
    logger.info('LiveKit connection successful', {
      roomName: token.roomName,
      roomType: token.roomType,
    });
    
    onConnect?.();
    
    if (token.coinUsage) {
      toast.success(
        `Connected to room! Used ${token.coinUsage.cost} coins. Balance: ${token.coinUsage.remainingBalance}`,
        { duration: 3000, id: 'livekit-connection' }
      );
    } else {
      toast.success('Connected to room!', { id: 'livekit-connection' });
    }
  }, [onConnect, isConnected]);

  // Handle connection error
  const handleConnectionError = useCallback((error: string) => {
    setIsConnecting(false);
    setIsConnected(false);
    setConnectionError(error);
    setRoomToken(null);
    setRoomName(null);
    
    logger.error('LiveKit connection failed:', error);
    
    onError?.(error);
    toast.error(`Connection failed: ${error}`);
  }, [onError]);

  // Create personal chat room
  const createPersonalChatRoom = useCallback(async (conversationId: string): Promise<boolean> => {
    if (!user) {
      handleConnectionError('User not authenticated');
      return false;
    }

    if (isConnecting || isConnected) {
      logger.info('Already connecting or connected, skipping duplicate request');
      return isConnected;
    }

    setIsConnecting(true);
    setConnectionError(null);

    try {
      logger.info('Creating personal chat room', { conversationId, userId: user.uid });
      
      const token = await livekitService.createPersonalChatRoom(conversationId);
      
      if (!token.success) {
        handleConnectionError(token.error || 'Failed to create personal chat room');
        return false;
      }

      handleConnectionSuccess(token);
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('Error in createPersonalChatRoom:', error);
      handleConnectionError(errorMessage);
      return false;
    }
  }, [user, handleConnectionSuccess, handleConnectionError, isConnecting, isConnected]);

  // Create group chat room
  const createGroupChatRoom = useCallback(async (
    groupId: string,
    participants: string[] = []
  ): Promise<boolean> => {
    if (!user) {
      handleConnectionError('User not authenticated');
      return false;
    }

    setIsConnecting(true);
    setConnectionError(null);

    try {
      const roomName = livekitService.generateGroupChatRoomName(groupId);
      
      const token = await livekitService.createRoom({
        roomName,
        roomType: 'group_compatibility',
        groupId,
        participants,
        maxParticipants: Math.max(participants.length + 2, 10),
      });
      
      if (!token.success) {
        handleConnectionError(token.error || 'Failed to create group chat room');
        return false;
      }

      handleConnectionSuccess(token);
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      handleConnectionError(errorMessage);
      return false;
    }
  }, [user, handleConnectionSuccess, handleConnectionError]);

  // Create voice consultation room
  const createVoiceConsultationRoom = useCallback(async (consultationId: string): Promise<boolean> => {
    if (!user) {
      handleConnectionError('User not authenticated');
      return false;
    }

    setIsConnecting(true);
    setConnectionError(null);

    try {
      const roomName = livekitService.generateVoiceConsultationRoomName(consultationId);
      
      const token = await livekitService.createRoom({
        roomName,
        roomType: 'voice_consultation',
        consultationId,
        maxParticipants: 2,
      });
      
      if (!token.success) {
        handleConnectionError(token.error || 'Failed to create voice consultation room');
        return false;
      }

      handleConnectionSuccess(token);
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      handleConnectionError(errorMessage);
      return false;
    }
  }, [user, handleConnectionSuccess, handleConnectionError]);

  // Join existing room
  const joinRoom = useCallback(async (
    roomNameToJoin: string,
    participantName?: string
  ): Promise<boolean> => {
    if (!user) {
      handleConnectionError('User not authenticated');
      return false;
    }

    setIsConnecting(true);
    setConnectionError(null);

    try {
      const token = await livekitService.joinRoom({
        roomName: roomNameToJoin,
        participantName,
        canPublish: true,
        canSubscribe: true,
        canPublishData: true,
      });
      
      if (!token.success) {
        handleConnectionError(token.error || 'Failed to join room');
        return false;
      }

      handleConnectionSuccess(token);
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      handleConnectionError(errorMessage);
      return false;
    }
  }, [user, handleConnectionSuccess, handleConnectionError]);

  // Disconnect from room
  const disconnect = useCallback(() => {
    logger.info('Disconnecting from LiveKit room', { roomName });
    
    resetConnectionState();
    onDisconnect?.();
    toast.success('Disconnected from room');
  }, [roomName, resetConnectionState, onDisconnect]);

  // Health check
  const healthCheck = useCallback(async (): Promise<boolean> => {
    try {
      const result = await livekitService.healthCheck();
      
      if (result.success) {
        logger.info('LiveKit service health check passed');
        return true;
      } else {
        logger.warn('LiveKit service health check failed', { error: result.error });
        return false;
      }
    } catch (error) {
      logger.error('LiveKit health check error:', error);
      return false;
    }
  }, []);

  // Auto-connect effect
  useEffect(() => {
    if (autoConnect && user && !isConnected && !isConnecting) {
      logger.info('Auto-connecting to LiveKit service');
      healthCheck();
    }
  }, [autoConnect, user, isConnected, isConnecting, healthCheck]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isConnected) {
        logger.info('Cleaning up LiveKit connection on unmount');
        resetConnectionState();
      }
    };
  }, [isConnected, resetConnectionState]);

  return {
    // Connection state
    isConnecting,
    isConnected,
    connectionError,
    
    // Room data
    roomToken,
    roomName,
    
    // Actions
    createPersonalChatRoom,
    createGroupChatRoom,
    createVoiceConsultationRoom,
    joinRoom,
    disconnect,
    resetConnectionState,
    
    // Utilities
    healthCheck,
  };
};

export default useLiveKit;
